import { useMemo } from 'react';
import { Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import RangeRingLabel from './RangeRingLabel';
import { getColourSettings, UNIT_SYSTEMS, intervalToKm } from '../utils/mapStyles';
import { useUnitsSettings } from '../contexts/SettingsContext';
import './ReceiverMarker.css';

// Create custom receiver icon using antenna.svg (same as History page)
const createReceiverIcon = () => {
  const iconHtml = `<img src="/antenna.svg" style="width: 24px; height: 24px;" />`;

  return L.divIcon({
    html: iconHtml,
    className: 'receiver-marker',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });
};

function ReceiverMarker({ receiver, selectedReceiver, colourSettings }) {
  // Get colour settings (use prop if provided, otherwise load from localStorage)
  const colours = colourSettings || getColourSettings();

  // Get units settings from context (single interval at app level)
  const unitsSettings = useUnitsSettings();

  // Cache the icon so it doesn't get recreated on every render
  // Must call hooks before any early returns
  const receiverIcon = useMemo(
    () => createReceiverIcon(receiver.receiverName),
    [receiver.receiverName]
  );

  if (!receiver.latitude || !receiver.longitude) {
    return null;
  }

  const position = [receiver.latitude, receiver.longitude];

  // Determine if this receiver's range rings should be shown: the rings belong
  // to the selected receiver (or all, when nothing specific is selected) AND
  // the map's range-rings overlay toggle is on.
  const showRangeRings = (selectedReceiver === 'All Receivers' || selectedReceiver === receiver.receiverName)
    && colours.showRangeRings !== false;

  // Get range ring configuration from global settings
  const rangeRingsNumber = unitsSettings.rangeRingsCount;
  const rangeRingsInterval = unitsSettings.rangeRingsInterval;
  const distanceUnits = unitsSettings.units;

  // Get the unit system for labels
  const unitSystem = UNIT_SYSTEMS[distanceUnits] || UNIT_SYSTEMS.metric;
  const unitLabel = unitSystem.distance;

  // Convert interval to meters (intervalToKm returns km, then convert to meters)
  const intervalInKm = intervalToKm(rangeRingsInterval, distanceUnits);
  const metersPerInterval = intervalInKm * 1000;

  // Generate range rings only if they should be shown
  const rangeRings = [];
  if (showRangeRings) {
    for (let i = 1; i <= rangeRingsNumber; i++) {
      const distance = i * rangeRingsInterval; // Distance in selected unit (km, mi, or nm)
      const radiusInMeters = i * metersPerInterval; // Each ring is i intervals away

      rangeRings.push({
        radius: radiusInMeters,
        distance: distance,
        label: `${distance} ${unitLabel}`
      });
    }
  }

  return (
    <>
      {/* Range rings */}
      {rangeRings.map((ring, index) => (
        <Circle
          key={`ring-${index}`}
          center={position}
          radius={ring.radius}
          pathOptions={{
            color: colours.rangeRings,
            fillColor: 'transparent',
            fillOpacity: 0,
            weight: 2,
            opacity: 0.85
          }}
          pane="shadowPane"
        />
      ))}

      {/* Range ring labels */}
      {rangeRings.map((ring, index) => (
        <RangeRingLabel
          key={`label-${index}`}
          center={position}
          radius={ring.radius}
          label={ring.label}
        />
      ))}

      <Marker position={position} icon={receiverIcon}>
        <Popup>
          <div style={{ minWidth: '200px' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
              {receiver.receiverName}
            </h3>

            <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
              <div><strong>Location:</strong> {receiver.latitude.toFixed(4)}, {receiver.longitude.toFixed(4)}</div>

              {receiver.airportIcao && (
                <div><strong>Airport:</strong> {receiver.airportIcao}</div>
              )}

              {receiver.receiverType && (
                <div><strong>Type:</strong> {receiver.receiverType}</div>
              )}

              {receiver.ipAddress && (
                <div><strong>IP:</strong> {receiver.ipAddress}</div>
              )}

              {rangeRingsNumber > 0 && (
                <div><strong>Range Rings:</strong> {rangeRingsNumber} × {rangeRingsInterval} {unitLabel}</div>
              )}

              <div style={{
                marginTop: '8px',
                padding: '4px 8px',
                borderRadius: '4px',
                background: receiver.isActive ? '#d4edda' : '#f8d7da',
                color: receiver.isActive ? '#155724' : '#721c24',
                fontWeight: '600',
                fontSize: '12px'
              }}>
                {receiver.isActive ? 'ACTIVE' : 'INACTIVE'}
              </div>
            </div>
          </div>
        </Popup>
      </Marker>
    </>
  );
}

export default ReceiverMarker;
